import { useEffect, useState } from "react";

export const inputCls =
  "w-full bg-white border border-parchborder rounded-xl px-3.5 py-2.5 text-sm outline-none focus:border-dill transition-colors";

// Same styling WITHOUT w-full — for inputs that carry their own width/flex class.
// (w-full would override w-28 etc. depending on generated CSS order.)
export const inputFitCls =
  "bg-white border border-parchborder rounded-xl px-3.5 py-2.5 text-sm outline-none focus:border-dill transition-colors";

export const money = (n) =>
  `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const todayISO = () => new Date().toISOString().slice(0, 10);

export function rangeFor(preset, custom) {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const iso = (d) => d.toISOString().slice(0, 10);
  if (preset === "month") return { start: iso(new Date(y, m, 1)), end: iso(now) };
  if (preset === "quarter") return { start: iso(new Date(y, Math.floor(m / 3) * 3, 1)), end: iso(now) };
  if (preset === "ytd") return { start: `${y}-01-01`, end: iso(now) };
  if (preset === "year") return { start: `${y}-01-01`, end: `${y}-12-31` };
  return { start: custom.start || "2000-01-01", end: custom.end || "2999-12-31" };
}

const PRESETS = [
  ["month", "This Month"],
  ["quarter", "This Quarter"],
  ["ytd", "Year to Date"],
  ["year", "Full Year"],
  ["custom", "Custom"],
];

export const RangeSelector = ({ onChange }) => {
  const [preset, setPreset] = useState("month");
  const [start, setStart] = useState(todayISO().slice(0, 8) + "01");
  const [end, setEnd] = useState(todayISO());

  useEffect(() => {
    onChange(rangeFor(preset, { start, end }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset, start, end]);

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="books-range-selector">
      {PRESETS.map(([id, label]) => (
        <button
          key={id}
          onClick={() => setPreset(id)}
          className={`rounded-full px-4 py-2 text-xs font-mono uppercase tracking-widest transition-colors border ${
            preset === id
              ? "bg-dill text-parchment border-dill"
              : "text-ink/55 border-parchborder hover:border-terra hover:text-terra"
          }`}
          data-testid={`range-${id}`}
        >
          {label}
        </button>
      ))}
      {preset === "custom" && (
        <>
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className={`${inputFitCls} w-auto`} data-testid="range-custom-start" />
          <span className="text-ink/40 text-sm">to</span>
          <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className={`${inputFitCls} w-auto`} data-testid="range-custom-end" />
        </>
      )}
    </div>
  );
};

export const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/50 block mb-1.5">{label}</span>
    {children}
  </label>
);

export const Card = ({ title, desc, children, testid }) => (
  <div className="bg-white border border-parchborder rounded-2xl p-6 space-y-4" data-testid={testid}>
    {(title || desc) && (
      <div>
        {title && <h3 className="font-serif font-bold text-lg text-dill">{title}</h3>}
        {desc && <p className="text-xs text-ink/50 mt-0.5">{desc}</p>}
      </div>
    )}
    {children}
  </div>
);

export const StatCard = ({ label, value, tone = "text-dill", testid }) => (
  <div className="bg-white border border-parchborder rounded-2xl p-5" data-testid={testid}>
    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/50 mb-1.5">{label}</p>
    <p className={`font-serif font-black text-2xl ${tone}`}>{value}</p>
  </div>
);

export const EstimateNote = () => (
  <p className="font-mono text-[10px] uppercase tracking-widest text-terra bg-terra/10 rounded-xl px-4 py-3 leading-relaxed">
    All tax figures are ESTIMATES ONLY — not tax advice. Please verify your obligations with a qualified tax professional.
  </p>
);

export const downloadFile = (filename, content, type = "application/json") => {
  const blob = new Blob([content], { type });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
};

export const downloadCSV = (filename, rows) => {
  const csv = rows
    .map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(","))
    .join("\n");
  downloadFile(filename, csv, "text/csv");
};
